import { useEffect, useRef } from "react";
import { Info, User, Clock, HelpCircle, StickyNote, CheckCircle2, Wrench, MessageSquare, Mail } from "lucide-react";
import { AttachmentGallery } from "./AttachmentGallery";

function fmtTime(iso) {
  try {
    return new Intl.DateTimeFormat("de-DE", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit", timeZone: "Europe/Berlin" }).format(new Date(iso));
  } catch {
    return iso;
  }
}

// Welche Aktion ein Nutzer im Verlauf durchgefuehrt hat (Daten aus den Ticketantworten).
const ACTION_STYLE = {
  accept: { label: "In Bearbeitung", Icon: Clock, color: "#C2410C", bg: "#FFF7ED", border: "#FED7AA" },
  question: { label: "Rückfrage", Icon: HelpCircle, color: "#C2410C", bg: "#FFF7ED", border: "#FED7AA" },
  note: { label: "Notiz", Icon: StickyNote, color: "#475569", bg: "#F1F5F9", border: "#E2E8F0" },
  complete: { label: "Erledigt", Icon: CheckCircle2, color: "#15803D", bg: "#F0FDF4", border: "#86EFAC" },
  request_external_service: { label: "Fachfirma beauftragen", Icon: Wrench, color: "#1D4ED8", bg: "#EFF6FF", border: "#BFDBFE" },
  email_reply: { label: "E-Mail-Antwort", Icon: Mail, color: "#6D28D9", bg: "#F5F3FF", border: "#DDD6FE" },
};

function actionStyle(e) {
  if (e.action && ACTION_STYLE[e.action]) return ACTION_STYLE[e.action];
  if (e.actionLabel) return { label: e.actionLabel, Icon: MessageSquare, color: "#475569", bg: "#F1F5F9", border: "#E2E8F0" };
  return null;
}
export const Timeline = ({ entries, token }) => {
  const endRef = useRef(null);
  useEffect(() => {
    if (endRef.current) endRef.current.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, [entries.length]);

  return (
    <ol className="flex flex-col gap-3 list-none p-0 m-0" data-testid="timeline">
      {entries.map((e) => {
        const isSystem = e.type === "status" || e.authorType === "system";
        if (isSystem) {
          return (
            <li key={e.id} className="wd-fade-in flex items-center gap-2 text-xs py-1" style={{ color: "#64748B" }} data-testid={`timeline-system-${e.id}`}>
              <Info size={14} className="flex-shrink-0" aria-hidden="true" />
              <span>{e.text}</span>
              <span className="ml-auto whitespace-nowrap">{fmtTime(e.createdAt)}</span>
            </li>
          );
        }
        const isReporter = e.authorType === "reporter";
        const act = isReporter ? null : actionStyle(e);
        const ActIcon = act?.Icon;
        return (
          <li key={e.id} className={`wd-fade-in flex ${isReporter ? "justify-start" : "justify-end"}`} data-testid={`timeline-msg-${e.id}`}>
            <div className="rounded-2xl px-4 py-3" style={{ maxWidth: "85%", background: isReporter ? "#F1F5F9" : "#FFF7ED", border: `1px solid ${isReporter ? "#E2E8F0" : "#FED7AA"}` }}>
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <span className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: isReporter ? "#E2E8F0" : "#FDBA74" }}>
                  <User size={13} color={isReporter ? "#475569" : "#7C2D12"} aria-hidden="true" />
                </span>
                <span className="text-xs font-bold" data-testid={`timeline-author-${e.id}`}>{e.authorName || (isReporter ? "Meldende Stelle" : "Hausmeister")}</span>
                {act ? (
                  <span data-testid={`timeline-action-${e.id}`} className="inline-flex items-center gap-1 text-xs font-bold px-2 py-0.5 rounded-full" style={{ background: act.bg, color: act.color, border: `1px solid ${act.border}` }}>
                    {ActIcon && <ActIcon size={11} />} {act.label}
                  </span>
                ) : (
                  <span className="text-xs" style={{ color: "#94A3B8" }}>· {isReporter ? "meldende Stelle" : "Hausmeister"}</span>
                )}
                <span className="text-xs ml-auto whitespace-nowrap" style={{ color: "#94A3B8" }}>{fmtTime(e.createdAt)}</span>
              </div>
              <p className="text-sm m-0" style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{e.text}</p>
              {e.action === "question" && e.recipients?.length > 0 && (
                <p className="text-xs mt-1.5 mb-0 flex items-start gap-1" style={{ color: "#94A3B8" }} data-testid={`timeline-recipients-${e.id}`}>
                  <Mail size={11} className="flex-shrink-0 mt-0.5" aria-hidden="true" />
                  <span style={{ wordBreak: "break-word" }}>Gesendet an: {e.recipients.join(", ")}</span>
                </p>
              )}
              {token && e.attachments?.length > 0 && (
                <AttachmentGallery attachments={e.attachments} token={token} mid={e.id} />
              )}
            </div>
          </li>
        );
      })}
      <li ref={endRef} aria-hidden="true" />
    </ol>
  );
};
